import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, Fragment as _Fragment, createTextVNode as _createTextVNode, withCtx as _withCtx } from "vue";
const _hoisted_1 = { class: "flex flex-col gap-3" };
const _hoisted_2 = { class: "flex items-center justify-between gap-2" };
const _hoisted_3 = { class: "flex items-center gap-2" };
const _hoisted_4 = { class: "flex flex-col gap-0.5" };
const _hoisted_5 = { class: "flex items-center gap-2" };
const _hoisted_6 = { class: "flex gap-3 text-(--text-text-body)" };
const _hoisted_7 = {
    key: 0,
    class: "flex min-w-px flex-1 flex-col",
    "data-testid": "tax-receipt-card-covered-year"
};
const _hoisted_8 = { class: "text-xs leading-5 font-medium" };
const _hoisted_9 = { class: "text-sm leading-5 font-semibold" };
const _hoisted_10 = { class: "flex min-w-px flex-1 flex-col" };
const _hoisted_11 = { class: "text-xs leading-5 font-medium" };
const _hoisted_12 = { class: "text-sm leading-5 font-semibold" };
const _hoisted_13 = { class: "flex min-w-px flex-1 flex-col" };
const _hoisted_14 = { class: "text-xs leading-5 font-medium" };
const _hoisted_15 = { class: "text-sm leading-5 font-semibold" };
const _hoisted_16 = { class: "flex min-w-px flex-1 flex-col" };
const _hoisted_17 = { class: "text-xs leading-5 font-medium" };
const _hoisted_18 = { class: "text-sm leading-5 font-semibold" };
const _hoisted_19 = { class: "flex min-w-px flex-1 flex-col" };
const _hoisted_20 = { class: "text-xs leading-5 font-medium" };
const _hoisted_21 = { class: "text-sm leading-5 font-semibold" };
const _hoisted_22 = {
    key: 1,
    class: "flex min-w-px flex-1 flex-col",
    "data-testid": "tax-receipt-card-cancellation-date"
};
const _hoisted_23 = { class: "text-xs leading-5 font-medium" };
const _hoisted_24 = { class: "text-sm leading-5 font-semibold" };
const _hoisted_25 = {
    key: 0,
    class: "text-xs leading-5 font-medium text-(--text-text-error-strong)",
    "data-testid": "tax-receipt-card-email-failure"
};
const _hoisted_26 = {
    class: "flex flex-wrap items-center justify-between gap-2 text-(--text-text-body)",
    "data-testid": "tax-receipt-public-link"
};
const _hoisted_27 = { class: "flex flex-col" };
const _hoisted_28 = { class: "text-xs leading-5 font-medium" };
const _hoisted_29 = {
    class: "text-sm leading-5 font-semibold",
    "data-testid": "tax-receipt-public-link-valid-until"
};
const _hoisted_30 = { class: "flex items-center gap-2" };
const _hoisted_31 = { class: "text-sm text-(--text-text-body)" };
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { Badge, Button, IconShape, Modal } from "@eudonet/opal";
import { useAppToast } from "@/shared/composables/useAppToast";
import { useFinanceFormatters } from "@/shared/composables/useFinanceFormatters";
import { taxReceiptEmailFailureReason } from "../../../domain/entities/taxReceiptEmailFailure";
/** Maps the backend lifecycle status to the fiche's display status (Failed → NotIssued). */
export default /*@__PURE__*/ _defineComponent({
    __name: 'TaxReceiptCard',
    props: {
        receipt: {},
        currency: { default: undefined },
        resending: { type: Boolean, default: false },
        cancelling: { type: Boolean, default: false },
        revokingLink: { type: Boolean, default: false },
        readonly: { type: Boolean, default: false },
        cancellable: { type: Boolean, default: false }
    },
    emits: ["download", "resend", "cancel", "revoke-link"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const { t } = useI18n();
        const { toastSuccess, toastError } = useAppToast();
        const { formatAmount, formatDate } = useFinanceFormatters();
        const displayStatus = computed(() => {
            switch (props.receipt.status) {
                case "Generated":
                    return "Issued";
                case "Sent":
                    return "Sent";
                case "Cancelled":
                    return "Cancelled";
                default:
                    return "NotIssued";
            }
        });
        const isCancelled = computed(() => displayStatus.value === "Cancelled");
        // An annual receipt is numbered on the year after the one it covers.
        const coveredYear = computed(() => props.receipt.mode === "AnnualGrouped" ? props.receipt.receiptYear - 1 : null);
        // Per the PO ruling on the multi-receipt fiche: actions are active only on an
        // émis (issued/sent) receipt — every button renders disabled on the others,
        // download included, even though a cancelled PDF technically stays downloadable.
        const isActionable = computed(() => displayStatus.value === "Issued" || displayStatus.value === "Sent");
        const canDownload = computed(() => isActionable.value && Boolean(props.receipt.pdfUrl));
        // Shown for every status, cancelled included: the link serves the cancelled copy of the receipt.
        const hasPublicLink = computed(() => Boolean(props.receipt.publicLinkUrl));
        // The last email attempt that failed, as OPDON recorded it (OP-8127 rule 7); cleared by a
        // successful send, so a Sent receipt shows it only after a failed re-send.
        const emailFailureText = computed(() => {
            const code = props.receipt.lastEmailFailureCode;
            if (!code)
                return null;
            // The email may have gone out: "failed" would invite a duplicate resend.
            const sentence = code === "TaxReceipt.Email.OutcomeUnknown"
                ? "finance.donations.taxReceipt.lastEmailOutcomeUnknown"
                : "finance.donations.taxReceipt.lastEmailFailure";
            return t(sentence, {
                date: formatDate(props.receipt.lastEmailFailedAt ?? undefined),
                reason: taxReceiptEmailFailureReason(t, code, props.receipt.lastEmailFailureDetail),
            });
        });
        const showRevokeConfirm = ref(false);
        async function copyPublicLink() {
            const url = props.receipt.publicLinkUrl;
            if (!url)
                return;
            try {
                await navigator.clipboard.writeText(url);
                toastSuccess("finance.donations.taxReceipt.publicLink.copied");
            }
            catch {
                toastError("finance.donations.taxReceipt.publicLink.copyFailed");
            }
        }
        function confirmRevoke() {
            showRevokeConfirm.value = false;
            emit("revoke-link", props.receipt);
        }
        const STATUS_THEME = {
            NotIssued: "red",
            Issued: "green",
            Sent: "indigo",
            Cancelled: "neutral",
        };
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("article", {
                class: _normalizeClass(["rounded-(--border-border-radius-rounded-l) border border-(--border-border-base-medium) p-6",
                    isCancelled.value
                        ? 'bg-(--colors-background-white-secondary)'
                        : 'bg-(--colors-background-white) shadow-(--blur-shadow-s)'
                ]),
                "data-testid": "tax-receipt-card"
            }, [
                _createElementVNode("div", _hoisted_1, [
                    _createElementVNode("div", _hoisted_2, [
                        _createElementVNode("div", _hoisted_3, [
                            _createVNode(_unref(IconShape), {
                                size: "lg",
                                type: "square",
                                variant: "outlined",
                                color: isCancelled.value ? 'disabled' : 'indigo',
                                icon: "ri-file-list-3-line"
                            }, null, 8 /* PROPS */, ["color"]),
                            _createElementVNode("div", _hoisted_4, [
                                _createElementVNode("h3", {
                                    class: _normalizeClass(["text-base leading-5 font-bold",
                                        isCancelled.value
                                            ? 'line-through text-(--text-text-disabled-strong)'
                                            : 'text-(--text-text-heading)'
                                    ]),
                                    "data-testid": "tax-receipt-card-title"
                                }, _toDisplayString(_unref(t)("finance.donations.taxReceipt.cardTitle", { number: __props.receipt.receiptNumber })), 3 /* TEXT, CLASS */),
                                _createVNode(_unref(Badge), {
                                    theme: STATUS_THEME[displayStatus.value],
                                    label: _unref(t)(`finance.donations.taxReceipt.statusValue.${displayStatus.value}`),
                                    "show-close-icon": false,
                                    size: "xs",
                                    "data-testid": "tax-receipt-card-status"
                                }, null, 8 /* PROPS */, ["theme", "label"])
                            ])
                        ]),
                        _createElementVNode("div", _hoisted_5, [
                            _createVNode(_unref(Button), {
                                variant: "secondary",
                                size: "sm",
                                "icon-left": "ri-file-download-line",
                                label: _unref(t)('finance.donations.taxReceipt.downloadPdf'),
                                disabled: !canDownload.value,
                                "data-testid": "tax-receipt-card-download",
                                onClick: _cache[0] || (_cache[0] = ($event) => (emit('download', __props.receipt)))
                            }, null, 8 /* PROPS */, ["label", "disabled"]),
                            (!__props.readonly)
                                ? (_openBlock(), _createBlock(_unref(Button), {
                                    key: 0,
                                    variant: "secondary",
                                    size: "sm",
                                    "icon-left": "ri-mail-send-line",
                                    label: _unref(t)(__props.receipt.sentAt ? 'finance.donations.taxReceipt.resendEmail' : 'finance.donations.taxReceipt.sendEmail'),
                                    disabled: !isActionable.value,
                                    loading: __props.resending,
                                    "data-testid": "tax-receipt-card-resend",
                                    onClick: _cache[1] || (_cache[1] = ($event) => (emit('resend', __props.receipt)))
                                }, null, 8 /* PROPS */, ["label", "disabled", "loading"]))
                                : _createCommentVNode("v-if", true),
                            (!__props.readonly || __props.cancellable)
                                ? (_openBlock(), _createBlock(_unref(Button), {
                                    key: 1,
                                    variant: "error-secondary",
                                    size: "sm",
                                    label: _unref(t)('finance.donations.taxReceipt.cancel'),
                                    disabled: !isActionable.value,
                                    loading: __props.cancelling,
                                    "data-testid": "tax-receipt-card-cancel",
                                    onClick: _cache[2] || (_cache[2] = ($event) => (emit('cancel', __props.receipt)))
                                }, null, 8 /* PROPS */, ["label", "disabled", "loading"]))
                                : _createCommentVNode("v-if", true)
                        ])
                    ]),
                    _cache[7] || (_cache[7] = _createElementVNode("hr", { class: "border-0 border-t border-(--border-border-base-soft)" }, null, -1 /* CACHED */)),
                    _createElementVNode("div", _hoisted_6, [
                        (coveredYear.value !== null)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                                _createElementVNode("span", _hoisted_8, _toDisplayString(_unref(t)("finance.donations.taxReceipt.coveredYear")), 1 /* TEXT */),
                                _createElementVNode("span", _hoisted_9, _toDisplayString(coveredYear.value), 1 /* TEXT */)
                            ]))
                            : _createCommentVNode("v-if", true),
                        _createElementVNode("div", _hoisted_10, [
                            _createElementVNode("span", _hoisted_11, _toDisplayString(_unref(t)("finance.donations.taxReceipt.eligibleAmount")), 1 /* TEXT */),
                            _createElementVNode("span", _hoisted_12, _toDisplayString(_unref(formatAmount)(__props.receipt.totalAmount, __props.currency)), 1 /* TEXT */)
                        ]),
                        _createElementVNode("div", _hoisted_13, [
                            _createElementVNode("span", _hoisted_14, _toDisplayString(_unref(t)("finance.donations.taxReceipt.issueDate")), 1 /* TEXT */),
                            _createElementVNode("span", _hoisted_15, _toDisplayString(_unref(formatDate)(__props.receipt.generatedAt ?? undefined)), 1 /* TEXT */)
                        ]),
                        _createElementVNode("div", _hoisted_16, [
                            _createElementVNode("span", _hoisted_17, _toDisplayString(_unref(t)("finance.donations.taxReceipt.sendDate")), 1 /* TEXT */),
                            _createElementVNode("span", _hoisted_18, _toDisplayString(_unref(formatDate)(__props.receipt.sentAt ?? undefined)), 1 /* TEXT */)
                        ]),
                        _createElementVNode("div", _hoisted_19, [
                            _createElementVNode("span", _hoisted_20, _toDisplayString(_unref(t)("finance.donations.taxReceipt.sendMode")), 1 /* TEXT */),
                            _createElementVNode("span", _hoisted_21, _toDisplayString(__props.receipt.sentAt ? _unref(t)("finance.donations.taxReceipt.sendModeEmail") : "—"), 1 /* TEXT */)
                        ]),
                        (isCancelled.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_22, [
                                _createElementVNode("span", _hoisted_23, _toDisplayString(_unref(t)("finance.donations.taxReceipt.cancellationDate")), 1 /* TEXT */),
                                _createElementVNode("span", _hoisted_24, _toDisplayString(_unref(formatDate)(__props.receipt.cancelledAt ?? undefined)), 1 /* TEXT */)
                            ]))
                            : _createCommentVNode("v-if", true)
                    ]),
                    (emailFailureText.value)
                        ? (_openBlock(), _createElementBlock("p", _hoisted_25, _toDisplayString(emailFailureText.value), 1 /* TEXT */))
                        : _createCommentVNode("v-if", true),
                    (hasPublicLink.value)
                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                            _cache[6] || (_cache[6] = _createElementVNode("hr", { class: "border-0 border-t border-(--border-border-base-soft)" }, null, -1 /* CACHED */)),
                            _createElementVNode("div", _hoisted_26, [
                                _createElementVNode("div", _hoisted_27, [
                                    _createElementVNode("span", _hoisted_28, _toDisplayString(_unref(t)("finance.donations.taxReceipt.publicLink.label")), 1 /* TEXT */),
                                    _createElementVNode("span", _hoisted_29, _toDisplayString(_unref(t)("finance.donations.taxReceipt.publicLink.validUntil", {
                                        date: _unref(formatDate)(__props.receipt.publicLinkExpiresAt ?? undefined),
                                    })), 1 /* TEXT */)
                                ]),
                                _createElementVNode("div", _hoisted_30, [
                                    _createVNode(_unref(Button), {
                                        variant: "secondary",
                                        size: "sm",
                                        "icon-left": "ri-links-line",
                                        label: _unref(t)('finance.donations.taxReceipt.publicLink.copy'),
                                        "data-testid": "tax-receipt-copy-link",
                                        onClick: copyPublicLink
                                    }, null, 8 /* PROPS */, ["label"]),
                                    (!__props.readonly)
                                        ? (_openBlock(), _createBlock(_unref(Button), {
                                            key: 0,
                                            variant: "error-secondary",
                                            size: "sm",
                                            label: _unref(t)('finance.donations.taxReceipt.publicLink.revoke'),
                                            loading: __props.revokingLink,
                                            disabled: __props.revokingLink,
                                            "data-testid": "tax-receipt-revoke-link",
                                            onClick: _cache[3] || (_cache[3] = ($event) => (showRevokeConfirm.value = true))
                                        }, null, 8 /* PROPS */, ["label", "loading", "disabled"]))
                                        : _createCommentVNode("v-if", true)
                                ])
                            ])
                        ], 64 /* STABLE_FRAGMENT */))
                        : _createCommentVNode("v-if", true)
                ]),
                _createVNode(_unref(Modal), {
                    modelValue: showRevokeConfirm.value,
                    "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event) => ((showRevokeConfirm).value = $event)),
                    size: "sm",
                    "data-testid": "tax-receipt-revoke-link-modal"
                }, {
                    header: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_unref(t)("finance.donations.taxReceipt.publicLink.revokeConfirm.title")), 1 /* TEXT */)
                    ]),
                    footer: _withCtx(() => [
                        _createVNode(_unref(Button), {
                            variant: "secondary",
                            label: _unref(t)('form.cancel'),
                            "data-testid": "tax-receipt-revoke-link-dismiss",
                            onClick: _cache[4] || (_cache[4] = ($event) => (showRevokeConfirm.value = false))
                        }, null, 8 /* PROPS */, ["label"]),
                        _createVNode(_unref(Button), {
                            variant: "error",
                            label: _unref(t)('finance.donations.taxReceipt.publicLink.revokeConfirm.confirm'),
                            "data-testid": "tax-receipt-revoke-link-confirm",
                            onClick: confirmRevoke
                        }, null, 8 /* PROPS */, ["label"])
                    ]),
                    default: _withCtx(() => [
                        _createElementVNode("p", _hoisted_31, _toDisplayString(_unref(t)("finance.donations.taxReceipt.publicLink.revokeConfirm.description")), 1 /* TEXT */)
                    ]),
                    _: 1 /* STABLE */
                }, 8 /* PROPS */, ["modelValue"])
            ], 2 /* CLASS */));
        };
    }
});
